import { BusinessUnit } from '#models/business-unit';
import { PSSR } from '#models/pssr';
import { User } from '#models/user';
import { Category } from '#models/category';
import { PSSRStatus } from '#models/pssr-status';
import { ResponseItem } from '#models/response-item';
import { ResponseItemStatus } from '#models/response-item-status';
import { SubCategory } from '#models/subcategory';
import { AuthService } from '#services/shared';
import { Utility } from '#services/shared/utility';
import { Injectable } from '@angular/core';
import { PSSRUserList } from '#models/pssr-user-list';
import { ActionItemAssiignedToType } from '#models/enum/action-item-assignedTo-type';

@Injectable()
export class ActionItemDialogService {
  constructor(private authService: AuthService) { }
  completeActionItem({
    actionItem,
    assignTo,
    queryParticipant = '',
    groups = [],
    isTraslator = false
  }: {
    actionItem: ResponseItem;
    assignTo: {
      assignToType: number;
      selectedUser: User;
      selectedCategory: Category;
      selectedSubCategory: SubCategory;
    };
    queryParticipant?: string;
    groups: any[];
    isTraslator: boolean;
  }): ResponseItem {
    if (!isTraslator && actionItem.Status === ResponseItemStatus.Closed) {
      // if ActionItem is marked as closed.
      const currentUser = this.authService.getCurrentUser();
      actionItem.ClosedBy = `${currentUser.FirstName} ${currentUser.LastName}`;
      actionItem.ClosedOn = new Date(new Date().getTime());
    }

    const { assignToType } = assignTo;
    if (assignToType == ActionItemAssiignedToType.PSSRMember) {
      actionItem.AssignedText = null;
      actionItem.AssignedGroupID = null;
      actionItem.AssignedGroupName = null;
    } else if (assignToType == ActionItemAssiignedToType.ChevronUsr) {
      const selectedUser = assignTo.selectedUser;
      actionItem.AssignedUserID = selectedUser.ID;
      actionItem.AssignedText = selectedUser.Email;
      actionItem.AssignedGroupID = null;
      actionItem.AssignedGroupName = null;
    } else if (assignToType == ActionItemAssiignedToType.Other) {
      actionItem.AssignedUserID = null;
      actionItem.AssignedGroupID = null;
      actionItem.AssignedGroupName = null;
    } else if (assignToType == ActionItemAssiignedToType.Group) {
      actionItem.AssignedUserID = null;
      actionItem.AssignedText = null;

      if (groups.length > 0) {
        const selectedGroup = groups.find(
          (x) => x.Id - actionItem.AssignedGroupID === 0
        );

        actionItem.AssignedGroupName = selectedGroup ? selectedGroup.Name : '';
      }
    }
    return actionItem;
  }
  getFullName(actionItem: ResponseItem): string {
    let fullName = actionItem.AssignedTo;
    if (actionItem.AssignedText) {
      fullName += this.isPrivateEmailDomain(actionItem.AssignedText)
        ? ' (Private)'
        : ' (Shared)';
      fullName += ` <${actionItem.AssignedText}>`;
    }

    return fullName;
  }

  // Compares the exact email domain rather than a substring match, since 'includes()'
  // would also match unrelated hosts like 'tengizchevroil.com.attacker.com' or 'nottengizchevroil.com'.
  private isPrivateEmailDomain(email: string): boolean {
    const domain = email.trim().toLowerCase().split('@').pop();
    return domain === 'tengizchevroil.com';
  }
  prepareActionItem({
    actionItem,

    pssr = {
      PSSRUsers: {
        Users: []
      } as PSSRUserList
    } as PSSR,
    selectedBU,
  }: {
    actionItem: ResponseItem;

    pssr: PSSR;
    selectedBU: BusinessUnit;
  }) {
    let selectedGroup;
    const assignTo = {
      selectedCategory: null,
      selectedSubCategory: null,
      assignToType: 1,
      selectedUser: null,
    };

    assignTo.selectedCategory = selectedBU.Categories.find(
      (c) => c.Id === actionItem.CategoryID
    );

    if (assignTo.selectedCategory) {
      assignTo.selectedSubCategory =
        assignTo.selectedCategory.SubCategories.find(
          (s) => s.Id === actionItem.SubCategoryID
        );
    }

    const pssrUser =
      pssr.PSSRUsers.Users.find(
        (f) => f.UserID == actionItem.AssignedUserID
      );

    if (pssrUser != null) {
      assignTo.assignToType = ActionItemAssiignedToType.PSSRMember; // PSSR MEMBER
    } else if (actionItem.AssignedUserID != null) {
      assignTo.assignToType = ActionItemAssiignedToType.ChevronUsr; // CAI
      assignTo.selectedUser = {
        ID: actionItem.AssignedUserID,
        FullName: this.getFullName(actionItem),
        Email: actionItem.AssignedText
      };
    } else if (actionItem.AssignedGroupID != null) {
      assignTo.assignToType = ActionItemAssiignedToType.Group; // GROUP
      selectedGroup = {
        Id: actionItem.AssignedGroupID,
        Name: actionItem.AssignedGroupName,
      };
    } else {
      assignTo.assignToType = ActionItemAssiignedToType.Other; // OTHER
    }

    return {
      assignTo,
      selectedGroup,
    };
  }
  setupActionItem({
    pssr,
    responseItem = {},
    responseId = null,
  }: {
    pssr: PSSR;
    responseId?: number;
    responseItem?: Partial<ResponseItem>;
  }): Partial<ResponseItem> {
    const defaultItems = {
      Action: '',
      DueDate: null,
      ActionID: 0,
      Status: ResponseItemStatus.Open,
      StatusName: 'Open',
      CreateDate: new Date(),
      ClosedBy: null,
      ClosedOn: null,
      Comment: '',
      CanEditItem: false,
      CanReOpenItem: false,
      IsActionItem: true,
      CanRejectItem: false,
      AfterWalkthrough: false,
      RejectReason: null,
      Id: 0,
      AssignedText: '',
      AssignedGroupID: 0,
      CategoryID: null,
      SubCategoryID: null,
      AssignedGroupName: '',
      AssignedTo: '',
      UpdatedDate: new Date(),
      Links: [],
      CurrentUserIsActionItemAssignee: false,
    } as ResponseItem;

    responseItem = { ...defaultItems, ...responseItem };
    const userId = Utility.isValidObj(responseItem) && responseItem.AssignedUserID > 0 ?
      responseItem.AssignedUserID :
      Utility.isValidObj(pssr.PSSRUsers.Users[0]) ?
        pssr.PSSRUsers.Users[0].UserID : 0;

    return {
      Priority: pssr.Status === PSSRStatus.Completed ? 3 : 4,
      AssignedUserID: userId,
      ...responseItem,
      PSSRID: pssr.Id,
      ResponseID: responseId,
      OrganizationId: pssr.OrganizationID,
    };
  }
  setupComment({ pssrId, responseId, organizationId }): Partial<ResponseItem> {
    return {
      Action: null,
      ActionID: 0,
      Status: ResponseItemStatus.None,
      StatusName: 'None',
      Priority: 0,
      CreateDate: new Date(),
      ClosedBy: null,
      ClosedOn: null,
      Comment: '',
      CanEditItem: false,
      CanReOpenItem: false,
      IsActionItem: false,
      CanRejectItem: false,
      Links: [],
      Id: 0,
      ResponseID: responseId,
      PSSRID: pssrId,
      OrganizationId: organizationId,
      UpdatedDate: new Date(),
    };
  }
}
